import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { CSS2DRenderer, CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import {
  setSelectedHotspot, setLight, setWindowPosition, setCurtainPosition,
  setFan, setAcEnabled, setPump, reasonMessage, getPlantProfiles,
} from '../mockStore.js';
import { t } from '../i18n.js';

/**
 * Logical hotspots — world anchors on scaled l128 footprint
 * (after sit-on-ground: model ~Y 0…1.85, long axis Z ±4, X ±0.9).
 * `position` = device target on/near the greenhouse; `pinOffset` = floating status pin.
 * Thick stem + arrow + circular CSS2D badge (name inside coloured dot): pin → target. Not BIM mesh-bound.
 */
export const HOTSPOTS = [
  // Windows — long walls (pins outward ±X)
  { id: 'w1', label: '窗1', kind: 'window', position: new THREE.Vector3(-0.95, 1.15, 3.2), pinOffset: new THREE.Vector3(-0.5, 0.2, 0.15) },
  { id: 'w2', label: '窗2', kind: 'window', position: new THREE.Vector3(-0.95, 1.15, 0.6), pinOffset: new THREE.Vector3(-0.5, 0.25, -0.1) },
  { id: 'w3', label: '窗3', kind: 'window', position: new THREE.Vector3(0.95, 1.15, 3.2), pinOffset: new THREE.Vector3(0.5, 0.2, 0.15) },
  { id: 'w4', label: '窗4', kind: 'window', position: new THREE.Vector3(0.95, 1.15, 0.6), pinOffset: new THREE.Vector3(0.5, 0.25, -0.1) },
  // Curtain — roof (pin up)
  { id: 'curtain', label: '屋頂簾', kind: 'curtain', position: new THREE.Vector3(0, 1.78, 0.5), pinOffset: new THREE.Vector3(0.15, 0.45, 0.2) },
  // Lights
  { id: 'white', label: '白光', kind: 'light-white', position: new THREE.Vector3(0, 0.55, 3.6), pinOffset: new THREE.Vector3(0.25, 0.35, 0.45) },
  { id: 'red', label: '紅光', kind: 'light-red', position: new THREE.Vector3(0, 1.45, -0.2), pinOffset: new THREE.Vector3(-0.4, 0.3, 0.15) },
  // Fans / AC — ends
  { id: 'fan-ex', label: '排風', kind: 'fan-exhaust', position: new THREE.Vector3(0, 1.35, -3.6), pinOffset: new THREE.Vector3(0.2, 0.25, -0.5) },
  { id: 'fan-ci', label: '循環扇', kind: 'fan-circulate', position: new THREE.Vector3(0.55, 1.5, 1.2), pinOffset: new THREE.Vector3(0.45, 0.2, 0.15) },
  { id: 'ac', label: '冷氣', kind: 'ac', position: new THREE.Vector3(-0.55, 1.4, -3.4), pinOffset: new THREE.Vector3(-0.45, 0.25, -0.35) },
  // Water — tank end (−Z cluster)
  { id: 'pump-irr', label: '灌溉泵', kind: 'pump-irrigation', position: new THREE.Vector3(-0.7, 0.45, -3.2), pinOffset: new THREE.Vector3(-0.45, 0.15, -0.35) },
  { id: 'valve', label: '街水閥', kind: 'valve-street', position: new THREE.Vector3(-0.35, 0.35, -3.5), pinOffset: new THREE.Vector3(0.15, 0.2, -0.5) },
  { id: 'pump-fert', label: '肥泵', kind: 'pump-fert', position: new THREE.Vector3(-0.55, 0.55, -2.7), pinOffset: new THREE.Vector3(-0.5, 0.25, 0.1) },
  { id: 'tank', label: '水箱', kind: 'sensor-tank', position: new THREE.Vector3(-0.85, 0.7, -2.4), pinOffset: new THREE.Vector3(-0.5, 0.35, 0) },
  // MCB — east door
  { id: 'mcb', label: '控制箱', kind: 'info', position: new THREE.Vector3(0.9, 0.85, 3.7), pinOffset: new THREE.Vector3(0.4, 0.2, 0.35) },
  // Sensors — mid / high (pins up / out)
  { id: 't1', label: '溫1', kind: 'sensor-temp', index: 0, position: new THREE.Vector3(-0.4, 1.55, 2.4), pinOffset: new THREE.Vector3(-0.35, 0.4, 0.2) },
  { id: 't2', label: '溫2', kind: 'sensor-temp', index: 1, position: new THREE.Vector3(0, 1.55, 0.2), pinOffset: new THREE.Vector3(0.3, 0.45, 0) },
  { id: 't3', label: '溫3', kind: 'sensor-temp', index: 2, position: new THREE.Vector3(0.4, 1.55, -2.0), pinOffset: new THREE.Vector3(0.35, 0.4, -0.15) },
  { id: 'tout', label: '外溫', kind: 'sensor-temp-out', position: new THREE.Vector3(1.05, 1.65, 0), pinOffset: new THREE.Vector3(0.5, 0.25, 0.1) },
  { id: 'hum', label: '濕度', kind: 'sensor-humidity', position: new THREE.Vector3(-0.5, 1.35, -0.8), pinOffset: new THREE.Vector3(-0.45, 0.3, -0.2) },
  { id: 'co2', label: 'CO₂', kind: 'sensor-co2', position: new THREE.Vector3(0.5, 1.35, -0.8), pinOffset: new THREE.Vector3(0.45, 0.3, -0.2) },
  { id: 'wind', label: '風速', kind: 'sensor-wind', position: new THREE.Vector3(0, 1.82, 2.0), pinOffset: new THREE.Vector3(0.2, 0.45, 0.25) },
  { id: 'lux', label: '照度', kind: 'sensor-lux', position: new THREE.Vector3(0, 1.72, -1.5), pinOffset: new THREE.Vector3(-0.2, 0.45, -0.25) },
];

const COLOR_ON = 0x22a06b;
const COLOR_OFF = 0xe03131;
const COLOR_FAULT_BASE = 0x8b1a1a;
const COLOR_RING = 0xffd43b;

let renderer, scene, camera, controls, animId, resizeObs;
/** @type {CSS2DRenderer | null} */
let labelRenderer = null;
/** @type {{ group: THREE.Group, sphere: THREE.Mesh, arrow: THREE.Mesh, cross: THREE.Group, ring: THREE.Mesh, stem: THREE.Mesh, stemMat: THREE.MeshBasicMaterial, arrowMat: THREE.MeshBasicMaterial, labelEl: HTMLElement, labelTextEl: HTMLElement, label: string, id: string }[]} */
let hotspotPins = [];
let hotspotMeshes = []; // pickables (pin spheres + arrow cones)
let currentUrl = '/models/demo-l128.glb';
let objectUrlToRevoke = null;
/** last state snapshot for animation-frame status refresh */
let lastStateRef = null;

function disposeSceneContent() {
  if (!scene) return;
  const keep = new Set(['__lights', '__ground']);
  [...scene.children].forEach((ch) => {
    if (ch.userData && keep.has(ch.userData.keep)) return;
    scene.remove(ch);
    ch.traverse?.((o) => {
      if (o.geometry) o.geometry.dispose();
      if (o.material) {
        if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
        else o.material.dispose();
      }
    });
  });
  hotspotPins = [];
  hotspotMeshes = [];
}

function makeCrossMark() {
  const g = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({ color: 0xff6b6b });
  const armGeo = new THREE.BoxGeometry(0.28, 0.045, 0.045);
  const a = new THREE.Mesh(armGeo, mat);
  a.rotation.z = Math.PI / 4;
  const b = new THREE.Mesh(armGeo, mat.clone());
  b.rotation.z = -Math.PI / 4;
  g.add(a, b);
  g.visible = false;
  return g;
}

const Y_UP = new THREE.Vector3(0, 1, 0);
const STEM_RADIUS = 0.022;
const CONE_RADIUS = 0.055;
const CONE_HEIGHT = 0.11;

/** Build a status-coloured cylinder from `from` → `to` (shortened for cone tip). */
function makeStemMesh(from, to) {
  const dir = new THREE.Vector3().subVectors(to, from);
  const full = dir.length();
  const stemLen = Math.max(0.04, full - CONE_HEIGHT * 0.55);
  const dirN = dir.clone().normalize();
  const geo = new THREE.CylinderGeometry(STEM_RADIUS, STEM_RADIUS, stemLen, 10);
  const mat = new THREE.MeshBasicMaterial({
    color: COLOR_OFF,
    transparent: true,
    opacity: 0.85,
    depthTest: true,
  });
  const mesh = new THREE.Mesh(geo, mat);
  const mid = from.clone().addScaledVector(dirN, stemLen / 2);
  mesh.position.copy(mid);
  mesh.quaternion.setFromUnitVectors(Y_UP, dirN);
  return { mesh, mat, dirN, stemLen };
}

function makeArrowCone(from, to) {
  const dir = new THREE.Vector3().subVectors(to, from);
  const full = dir.length();
  const stemLen = Math.max(0.04, full - CONE_HEIGHT * 0.55);
  const dirN = dir.clone().normalize();
  const geo = new THREE.ConeGeometry(CONE_RADIUS, CONE_HEIGHT, 12);
  const mat = new THREE.MeshBasicMaterial({
    color: COLOR_OFF,
    transparent: true,
    opacity: 0.85,
    depthTest: true,
  });
  const mesh = new THREE.Mesh(geo, mat);
  // Cone apex points +Y; place so tip sits at target
  mesh.position.copy(from).addScaledVector(dirN, stemLen + CONE_HEIGHT / 2);
  mesh.quaternion.setFromUnitVectors(Y_UP, dirN);
  return { mesh, mat };
}

function addHotspots() {
  hotspotPins = [];
  hotspotMeshes = [];

  for (const hs of HOTSPOTS) {
    const target = hs.position;
    const offset = hs.pinOffset || new THREE.Vector3(0, 0.4, 0);
    const pinPos = target.clone().add(offset);

    // Floating status pin: invisible hit-sphere + circular CSS2D badge (name inside colour)
    const group = new THREE.Group();
    group.position.copy(pinPos);
    group.userData.hotspotId = hs.id;

    // Invisible sphere for raycast picking (visual is the CSS badge)
    const geo = new THREE.SphereGeometry(0.16, 16, 16);
    const mat = new THREE.MeshBasicMaterial({
      color: COLOR_OFF,
      transparent: true,
      opacity: 0,
      depthTest: true,
      depthWrite: false,
    });
    const sphere = new THREE.Mesh(geo, mat);
    sphere.userData.hotspotId = hs.id;

    // Keep cross/ring stubs for API compat; fault ✕ + select ring are CSS on badge
    const cross = makeCrossMark();
    cross.visible = false;
    const ringGeo = new THREE.RingGeometry(0.14, 0.18, 24);
    const ringMat = new THREE.MeshBasicMaterial({
      color: COLOR_RING,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0,
      depthTest: true,
      depthWrite: false,
    });
    const ring = new THREE.Mesh(ringGeo, ringMat);
    ring.visible = false;

    const labelEl = document.createElement('div');
    labelEl.className = 'hs-label hs-label--off';
    labelEl.title = hs.label;
    const labelTextEl = document.createElement('span');
    labelTextEl.className = 'hs-label__text';
    labelTextEl.textContent = hs.label;
    labelEl.appendChild(labelTextEl);
    labelEl.dataset.text = hs.label;
    labelEl.style.pointerEvents = 'auto';
    labelEl.addEventListener('pointerdown', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      setSelectedHotspot(hs.id);
    });
    const labelObj = new CSS2DObject(labelEl);
    // Centre badge on the pin (text lives inside the coloured circle)
    labelObj.position.set(0, 0, 0);
    labelObj.center.set(0.5, 0.5);

    group.add(sphere, cross, ring, labelObj);
    scene.add(group);

    // Thick stem pin → target + arrow cone at target
    const { mesh: stem, mat: stemMat } = makeStemMesh(pinPos, target);
    stem.userData.hotspotId = hs.id;
    scene.add(stem);

    const { mesh: arrow, mat: arrowMat } = makeArrowCone(pinPos, target);
    arrow.userData.hotspotId = hs.id;
    scene.add(arrow);

    hotspotPins.push({
      group, sphere, arrow, cross, ring, stem, stemMat, arrowMat,
      labelEl, labelTextEl, label: hs.label, id: hs.id,
    });
    hotspotMeshes.push(sphere, arrow);
  }
}

/**
 * Resolve pin visual status from live house state.
 * @returns {'on'|'off'|'fault'}
 */
export function resolveHotspotStatus(hs, state) {
  if (!hs || !state) return 'off';
  const kind = hs.kind;

  if (kind === 'window') {
    const w = state.windows?.find((x) => x.id === hs.id);
    if (!w) return 'off';
    if (w.fault) return 'fault';
    return (w.position ?? 0) > 5 ? 'on' : 'off';
  }
  if (kind === 'curtain') {
    const c = state.curtain;
    if (!c) return 'off';
    if (c.fault) return 'fault';
    return (c.position ?? 0) > 5 ? 'on' : 'off';
  }
  if (kind === 'light-white') {
    const L = state.lights?.whiteWalkway;
    if (L?.fault) return 'fault';
    return L?.on ? 'on' : 'off';
  }
  if (kind === 'light-red') {
    const L = state.lights?.redGrow;
    if (L?.fault) return 'fault';
    return L?.on ? 'on' : 'off';
  }
  if (kind === 'fan-exhaust') {
    const f = state.fans?.exhaust;
    if (f?.fault) return 'fault';
    return f?.on ? 'on' : 'off';
  }
  if (kind === 'fan-circulate') {
    const f = state.fans?.circulate;
    if (f?.fault) return 'fault';
    return f?.on ? 'on' : 'off';
  }
  if (kind === 'ac') {
    const a = state.ac;
    if (a?.fault) return 'fault';
    return a?.enabled ? 'on' : 'off';
  }
  if (kind === 'pump-irrigation') {
    const p = state.water?.irrigationPump;
    if (p?.fault) return 'fault';
    return p?.on ? 'on' : 'off';
  }
  if (kind === 'valve-street') {
    const p = state.water?.streetValve;
    if (p?.fault) return 'fault';
    return p?.on ? 'on' : 'off';
  }
  if (kind === 'pump-fert') {
    const p = state.water?.fertPump;
    if (p?.fault) return 'fault';
    return p?.on ? 'on' : 'off';
  }
  if (kind === 'info') {
    const h = state.hardwareLocks || {};
    if (h.eStop || h.rcdTrip || h.overload || h.watchdogSafe || h.typhoonFieldLock) return 'off';
    return 'on';
  }

  // Sensors
  if (state.sensorFault) return 'fault';
  const profile = getPlantProfiles().find((p) => p.id === state.plantProfileId)
    || getPlantProfiles()[0];
  const [tLo, tHi] = profile?.temp || [18, 26];
  const [hLo, hHi] = profile?.humidity || [50, 75];
  const e = state.env || {};

  if (kind === 'sensor-temp') {
    const v = e.indoorTemp?.[hs.index ?? 0];
    if (v == null) return 'off';
    return v >= tLo && v <= tHi ? 'on' : 'off';
  }
  if (kind === 'sensor-temp-out') {
    // outdoor: mild band — red if extreme vs indoor comfort ±8
    const v = e.outdoorTemp;
    if (v == null) return 'off';
    return v >= tLo - 5 && v <= tHi + 8 ? 'on' : 'off';
  }
  if (kind === 'sensor-humidity') {
    const arr = e.indoorHumidity || [];
    const v = arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
    if (v == null) return 'off';
    return v >= hLo && v <= hHi ? 'on' : 'off';
  }
  if (kind === 'sensor-co2') {
    const v = e.co2Ppm ?? 0;
    return v > 1500 ? 'off' : 'on';
  }
  if (kind === 'sensor-wind') {
    const v = e.windMs ?? 0;
    return v >= 12 ? 'off' : 'on';
  }
  if (kind === 'sensor-lux') {
    const v = e.lightLux ?? 0;
    // very low night or extreme — treat mid as ok
    return v >= 50 && v <= 120000 ? 'on' : 'off';
  }
  if (kind === 'sensor-tank') {
    const pct = state.water?.tankLevelPct ?? 0;
    const low = state.water?.lowLevelThreshold ?? 15;
    return pct < low ? 'off' : 'on';
  }
  return 'off';
}

function applyPinVisual(pin, status, selected, anySelected) {
  const { sphere, cross, ring, group, stemMat, arrowMat, labelEl, labelTextEl, label } = pin;
  let statusColor = COLOR_OFF;
  if (status === 'fault') {
    statusColor = COLOR_FAULT_BASE;
    if (sphere?.material) sphere.material.color.setHex(COLOR_FAULT_BASE);
  } else if (status === 'on') {
    statusColor = COLOR_ON;
    if (sphere?.material) sphere.material.color.setHex(COLOR_ON);
  } else if (sphere?.material) {
    sphere.material.color.setHex(COLOR_OFF);
  }
  // Fault ✕ is drawn as CSS ::after on the badge; hide 3D cross
  if (cross) cross.visible = false;
  if (ring) ring.visible = false;

  const stemOp = selected ? 0.95 : (anySelected ? 0.45 : 0.85);
  if (stemMat) {
    stemMat.color.setHex(statusColor);
    stemMat.opacity = stemOp;
  }
  if (arrowMat) {
    arrowMat.color.setHex(statusColor);
    arrowMat.opacity = stemOp;
  }
  if (labelEl) {
    labelEl.classList.toggle('hs-label--selected', !!selected);
    labelEl.classList.toggle('hs-label--fault', status === 'fault');
    labelEl.classList.toggle('hs-label--on', status === 'on');
    labelEl.classList.toggle('hs-label--off', status === 'off');
    // Name stays inside the coloured circle; ✕ overlay via CSS when fault
    if (labelEl.dataset.text !== label) {
      labelEl.dataset.text = label;
      if (labelTextEl) labelTextEl.textContent = label;
      else labelEl.textContent = label;
    }
  }
  // Slightly enlarge selected badge (CSS also scales via --selected)
  group.scale.setScalar(selected ? 1.2 : 1);
}

function refreshHotspotVisuals(state) {
  if (!state || !hotspotPins.length) return;
  const anySelected = !!state.selectedHotspot;
  for (const pin of hotspotPins) {
    const hs = HOTSPOTS.find((h) => h.id === pin.id);
    const status = resolveHotspotStatus(hs, state);
    const selected = pin.id === state.selectedHotspot;
    applyPinVisual(pin, status, selected, anySelected);
    // Keep cross readable from orbit: face roughly toward camera
    if (camera && pin.cross.visible) {
      pin.cross.quaternion.copy(camera.quaternion);
    }
    if (pin.ring.visible && camera) {
      pin.ring.quaternion.copy(camera.quaternion);
    }
  }
}

function loadModel(url, statusEl) {
  if (statusEl) statusEl.textContent = '載入模型中…';
  disposeSceneContent();
  const loader = new GLTFLoader();
  loader.load(
    url,
    (gltf) => {
      const root = gltf.scene;
      root.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(root);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      root.position.sub(center);
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      const scale = 8 / maxDim;
      root.scale.setScalar(scale);
      // After center+scale, model Y is roughly ±half-height around origin; lift so it sits on y=0
      // (otherwise ground at y=-0.01 clips through mid-model and only the roof shows).
      root.updateMatrixWorld(true);
      const box2 = new THREE.Box3().setFromObject(root);
      root.position.y -= box2.min.y; // sit on y=0 → final min.y ≈ 0
      scene.add(root);
      addHotspots();
      if (lastStateRef) refreshHotspotVisuals(lastStateRef);
      if (camera) {
        camera.position.set(5, 2.8, 7);
        controls?.target.set(0, 1.0, 0);
        controls?.update();
      }
      if (statusEl) statusEl.textContent = `已載入：${url.split('/').pop()}`;
    },
    undefined,
    (err) => {
      console.error(err);
      addHotspots();
      if (lastStateRef) refreshHotspotVisuals(lastStateRef);
      if (statusEl) statusEl.textContent = `GLB 載入失敗，仍顯示 hotspot pins。${err?.message || ''}`;
    },
  );
}

function initThree(container) {
  const w = container.clientWidth || 640;
  const h = container.clientHeight || 400;
  scene = new THREE.Scene();
  scene.background = new THREE.Color(0xe8e8e8);

  camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 200);
  camera.position.set(5, 2.8, 7);

  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(w, h, false);
  container.appendChild(renderer.domElement);

  labelRenderer = new CSS2DRenderer();
  labelRenderer.setSize(w, h);
  labelRenderer.domElement.className = 'hs-label-layer';
  labelRenderer.domElement.style.position = 'absolute';
  labelRenderer.domElement.style.inset = '0';
  labelRenderer.domElement.style.pointerEvents = 'none';
  container.appendChild(labelRenderer.domElement);

  const hemi = new THREE.HemisphereLight(0xffffff, 0x444444, 1.25);
  hemi.userData.keep = '__lights';
  scene.add(hemi);
  const dir = new THREE.DirectionalLight(0xffffff, 1.0);
  dir.position.set(5, 10, 7);
  dir.userData.keep = '__lights';
  scene.add(dir);

  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(40, 40),
    new THREE.MeshStandardMaterial({ color: 0xdcdcdc, roughness: 0.95 }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -0.01;
  ground.userData.keep = '__ground';
  scene.add(ground);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.target.set(0, 1.0, 0);

  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  renderer.domElement.addEventListener('pointerdown', (ev) => {
    const rect = renderer.domElement.getBoundingClientRect();
    pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
    pointer.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(pointer, camera);
    const hits = raycaster.intersectObjects(hotspotMeshes, false);
    if (hits[0]) {
      setSelectedHotspot(hits[0].object.userData.hotspotId);
    }
  });

  const loop = () => {
    animId = requestAnimationFrame(loop);
    controls.update();
    if (lastStateRef) refreshHotspotVisuals(lastStateRef);
    renderer.render(scene, camera);
    labelRenderer?.render(scene, camera);
  };
  loop();

  resizeObs = new ResizeObserver(() => {
    if (!renderer || !container.isConnected) return;
    const nw = container.clientWidth;
    const nh = container.clientHeight;
    if (nw < 2 || nh < 2) return;
    camera.aspect = nw / nh;
    camera.updateProjectionMatrix();
    renderer.setSize(nw, nh, false);
    labelRenderer?.setSize(nw, nh);
  });
  resizeObs.observe(container);
}

function teardown() {
  if (animId) cancelAnimationFrame(animId);
  animId = null;
  resizeObs?.disconnect();
  resizeObs = null;
  controls?.dispose();
  controls = null;
  if (labelRenderer) {
    labelRenderer.domElement.remove();
    labelRenderer = null;
  }
  if (renderer) {
    renderer.dispose();
    renderer.domElement.remove();
    renderer = null;
  }
  scene = null;
  camera = null;
  hotspotPins = [];
  hotspotMeshes = [];
  lastStateRef = null;
  if (objectUrlToRevoke) {
    URL.revokeObjectURL(objectUrlToRevoke);
    objectUrlToRevoke = null;
  }
}

function statusBadge(status) {
  if (status === 'fault') return '<span class="meta" style="color:#e03131">✕ 故障</span>';
  if (status === 'on') return '<span class="meta" style="color:#22a06b">● 開</span>';
  return '<span class="meta" style="color:#e03131">● 停</span>';
}

function panelHtml(state, hs) {
  if (!hs) return `<div class="meta">撳 3D 色點揀設備（綠＝開 · 紅＝停 · ✕＝故障）</div>`;
  const typhoon = state.mode === 'typhoon';
  const st = resolveHotspotStatus(hs, state);
  const badge = statusBadge(st);

  if (hs.kind === 'window') {
    const w = state.windows.find((x) => x.id === hs.id);
    return `
      <h3>${w?.name || hs.label} ${badge}</h3>
      <div class="meta">位置 ${w?.position ?? 0}% ${w?.fault ? `· 故障：${w.fault}` : ''} ${typhoon ? '· 颱風鎖' : ''}</div>
      <div class="slider-row controls-only">
        <input type="range" min="0" max="100" value="${w?.position ?? 0}" data-hs-win="${hs.id}" ${typhoon || w?.fault ? 'disabled' : ''} />
        <span class="pct">${w?.position ?? 0}%</span>
      </div>`;
  }
  if (hs.kind === 'curtain') {
    return `
      <h3>${state.curtain.name} ${badge}</h3>
      <div class="meta">${state.curtain.fault ? `故障：${state.curtain.fault}` : `位置 ${state.curtain.position}%`}</div>
      <div class="slider-row controls-only">
        <input type="range" min="0" max="100" value="${state.curtain.position}" data-hs-curtain ${typhoon || state.curtain.fault ? 'disabled' : ''} />
        <span class="pct">${state.curtain.position}%</span>
      </div>`;
  }
  if (hs.kind === 'light-white') {
    return `
      <h3>白光行人燈 ${badge}</h3>
      <div class="meta">獨立電路 white-walkway</div>
      <button type="button" class="btn sm controls-only ${state.lights.whiteWalkway.on ? 'primary' : ''}" data-hs-light="white" ${typhoon ? 'disabled' : ''}>
        ${state.lights.whiteWalkway.on ? 'ON' : 'OFF'}
      </button>`;
  }
  if (hs.kind === 'light-red') {
    return `
      <h3>紅光補光 ${badge}</h3>
      <div class="meta">獨立電路 red-grow</div>
      <button type="button" class="btn sm controls-only ${state.lights.redGrow.on ? 'accent' : ''}" data-hs-light="red" ${typhoon ? 'disabled' : ''}>
        ${state.lights.redGrow.on ? 'ON' : 'OFF'}
      </button>`;
  }
  if (hs.kind === 'fan-exhaust') {
    return `
      <h3>排風／抽氣扇 ${badge}</h3>
      <button type="button" class="btn sm controls-only ${state.fans.exhaust.on ? 'primary' : ''}" data-hs-fan="exhaust" ${typhoon ? 'disabled' : ''}>
        ${state.fans.exhaust.on ? 'ON' : 'OFF'}
      </button>`;
  }
  if (hs.kind === 'fan-circulate') {
    return `
      <h3>循環扇 ${badge}</h3>
      <button type="button" class="btn sm controls-only ${state.fans.circulate.on ? 'primary' : ''}" data-hs-fan="circulate" ${typhoon ? 'disabled' : ''}>
        ${state.fans.circulate.on ? 'ON' : 'OFF'}
      </button>`;
  }
  if (hs.kind === 'ac') {
    return `
      <h3>冷氣使能 ${badge}</h3>
      <div class="meta">開啟時排風強制關</div>
      <button type="button" class="btn sm controls-only ${state.ac.enabled ? 'primary' : ''}" data-hs-ac ${typhoon ? 'disabled' : ''}>
        ${state.ac.enabled ? 'ON' : 'OFF'}
      </button>`;
  }
  if (hs.kind === 'pump-irrigation') {
    return `
      <h3>灌溉泵 ${badge}</h3>
      <button type="button" class="btn sm controls-only ${state.water.irrigationPump.on ? 'primary' : ''}" data-hs-pump="irrigation" ${typhoon ? 'disabled' : ''}>
        ${state.water.irrigationPump.on ? 'ON' : 'OFF'}
      </button>`;
  }
  if (hs.kind === 'valve-street') {
    return `
      <h3>街水閥 ${badge}</h3>
      <button type="button" class="btn sm controls-only ${state.water.streetValve.on ? 'primary' : ''}" data-hs-pump="street" ${typhoon ? 'disabled' : ''}>
        ${state.water.streetValve.on ? 'ON' : 'OFF'}
      </button>`;
  }
  if (hs.kind === 'pump-fert') {
    return `
      <h3>肥泵 ${badge}</h3>
      <button type="button" class="btn sm controls-only ${state.water.fertPump.on ? 'primary' : ''}" data-hs-pump="fert" ${typhoon ? 'disabled' : ''}>
        ${state.water.fertPump.on ? 'ON' : 'OFF'}
      </button>`;
  }
  if (hs.kind === 'sensor-tank') {
    return `
      <h3>水箱水位 ${badge}</h3>
      <div class="meta">${state.water.tankLevelPct}%（低水位門檻 ${state.water.lowLevelThreshold}%）· 唯讀</div>`;
  }
  if (hs.kind === 'sensor-temp') {
    const v = state.env.indoorTemp[hs.index ?? 0];
    return `
      <h3>室內溫 ${hs.label} ${badge}</h3>
      <div class="meta">${v?.toFixed?.(1) ?? v} °C · 唯讀</div>`;
  }
  if (hs.kind === 'sensor-temp-out') {
    return `
      <h3>室外溫 ${badge}</h3>
      <div class="meta">${state.env.outdoorTemp} °C · 唯讀</div>`;
  }
  if (hs.kind === 'sensor-humidity') {
    const arr = state.env.indoorHumidity;
    const avg = arr.reduce((a, b) => a + b, 0) / arr.length;
    return `
      <h3>室內濕度 ${badge}</h3>
      <div class="meta">平均 ${avg.toFixed(0)}%（${arr.join(' / ')}）· 唯讀</div>`;
  }
  if (hs.kind === 'sensor-co2') {
    return `
      <h3>CO₂ ${badge}</h3>
      <div class="meta">${state.env.co2Ppm} ppm（&gt;1500 警報）· 唯讀</div>`;
  }
  if (hs.kind === 'sensor-wind') {
    return `
      <h3>風速 ${badge}</h3>
      <div class="meta">${state.env.windMs} m/s（≥12 警報）· 唯讀</div>`;
  }
  if (hs.kind === 'sensor-lux') {
    return `
      <h3>照度 ${badge}</h3>
      <div class="meta">${state.env.lightLux} lux · 唯讀</div>`;
  }
  const locks = state.hardwareLocks || {};
  const lockBits = [
    locks.eStop && '急停',
    locks.rcdTrip && '漏電',
    locks.overload && '過載',
    locks.typhoonFieldLock && '颱風現場鎖',
    locks.watchdogSafe && '看門狗',
  ].filter(Boolean);
  return `
    <h3>MCB＋AI 控制箱 ${badge}</h3>
    <div class="meta">東端門口北邊乾區。市電／太陽能／柴油切換唔入 App。</div>
    <div class="hw-lock">${lockBits.length ? `硬件鎖：${lockBits.join('、')}` : '急停／漏電／主斷路器 — 硬件 only（目前正常）'}</div>`;
}

export function renderScene3d(root, state, { canControl }) {
  lastStateRef = state;
  const lang = state.lang;
  const selected = HOTSPOTS.find((h) => h.id === state.selectedHotspot) || null;
  const hadCanvas = !!root.querySelector('.scene-host canvas');

  if (!hadCanvas) {
    teardown();
    root.innerHTML = `
      <div class="view" style="max-width:none">
        <h2 class="view-title">${t(lang, 'scene3d')}</h2>
        <div class="btn-row" style="margin-bottom:8px">
          <button type="button" class="btn sm" data-reload-demo>載入示範 GLB</button>
          <label class="btn sm" style="cursor:pointer">上傳 glTF/GLB
            <input type="file" accept=".glb,.gltf,model/gltf-binary,model/gltf+json" hidden data-upload />
          </label>
          <span class="meta" data-model-status></span>
        </div>
        <div class="scene-wrap">
          <div class="scene-host" style="position:absolute;inset:0"></div>
          <div class="hotspot-panel" data-hs-panel></div>
        </div>
        <div class="meta" style="margin-top:8px" data-hs-legend>點内＝參數名 · 綠開紅停 · ✕故障 · 箭咀指位置</div>
        <div id="s3-toast" class="lock-reason" style="display:none;margin-top:8px"></div>
      </div>`;
    const host = root.querySelector('.scene-host');
    initThree(host);
    currentUrl = state.modelUrl || '/models/demo-l128.glb';
    loadModel(currentUrl, root.querySelector('[data-model-status]'));

    root.querySelector('[data-reload-demo]')?.addEventListener('click', () => {
      currentUrl = state.modelUrl || '/models/demo-l128.glb';
      loadModel(currentUrl, root.querySelector('[data-model-status]'));
    });
    root.querySelector('[data-upload]')?.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (!file) return;
      if (objectUrlToRevoke) URL.revokeObjectURL(objectUrlToRevoke);
      objectUrlToRevoke = URL.createObjectURL(file);
      currentUrl = objectUrlToRevoke;
      loadModel(currentUrl, root.querySelector('[data-model-status]'));
    });
  }

  const title = root.querySelector('.view-title');
  if (title) title.textContent = t(lang, 'scene3d');

  const legend = root.querySelector('[data-hs-legend]');
  if (legend) legend.textContent = '點内＝參數名 · 綠開紅停 · ✕故障 · 箭咀指位置';

  const panel = root.querySelector('[data-hs-panel]');
  if (panel) {
    panel.innerHTML = panelHtml(state, selected);
    const toast = root.querySelector('#s3-toast');
    function show(r) {
      if (!r) return;
      if (r.ok) { toast.style.display = 'none'; return; }
      toast.style.display = 'block';
      toast.textContent = reasonMessage(r.reason, lang);
    }
    panel.querySelector('[data-hs-win]')?.addEventListener('change', (ev) => {
      show(setWindowPosition(ev.target.dataset.hsWin, ev.target.value));
    });
    panel.querySelector('[data-hs-curtain]')?.addEventListener('change', (ev) => {
      show(setCurtainPosition(ev.target.value));
    });
    panel.querySelectorAll('[data-hs-light]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const c = btn.dataset.hsLight;
        const on = c === 'white' ? state.lights.whiteWalkway.on : state.lights.redGrow.on;
        show(setLight(c, !on));
      });
    });
    panel.querySelectorAll('[data-hs-fan]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const which = btn.dataset.hsFan;
        const on = which === 'exhaust' ? state.fans.exhaust.on : state.fans.circulate.on;
        show(setFan(which, !on));
      });
    });
    panel.querySelector('[data-hs-ac]')?.addEventListener('click', () => {
      show(setAcEnabled(!state.ac.enabled));
    });
    panel.querySelectorAll('[data-hs-pump]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const which = btn.dataset.hsPump;
        let on = false;
        if (which === 'irrigation') on = state.water.irrigationPump.on;
        else if (which === 'fert') on = state.water.fertPump.on;
        else if (which === 'street') on = state.water.streetValve.on;
        show(setPump(which, !on));
      });
    });
  }

  refreshHotspotVisuals(state);
}

export function destroyScene3d() {
  teardown();
}
